import type { DateIso, Phase } from '../../types/domain';
import { currentContractYear } from '../dates';
import { carriedInto } from './maintenanceCarryOver';
import { quotaUse, yearQuotaUse } from './quotaUse';
import type { MaintenanceLabelHours } from './types';

/* Quotas d'une maintenance, même règle sur la liste des projets et la fiche : correctif du mois
   civil en cours face au quota mensuel (arbitrage du 02/10/2026), et correctif cumulé sur l'année
   de contrat en cours face au quota annuel, car les heures non consommées se reportent (arbitrage
   du 14/10/2026) ; évolutif de l'année de contrat face au quota annuel ; les deux avec le report
   de l'année précédente. Temps correctif = tag maint_corrective pour une évolutive ou une maintenance qui
   partage son projet Jira, sinon tout le temps du projet sauf le tag non_facturable. Le reste du
   mois est « non facturable » ou « autre phase » (tracé). */
function correctiveHours(
  total: number | null | undefined,
  corr: number | null | undefined,
  nonFact: number | null | undefined,
  tagged: boolean,
): number | null {
  if (tagged) return corr ?? null;
  return total == null ? null : Math.max(0, total - (nonFact || 0));
}

// Heures du mois par catégorie.
function monthHours(lbl: MaintenanceLabelHours, month: string, isEvol: boolean, shared: boolean) {
  const of = (byMonth?: Record<string, number>) => (byMonth && byMonth[month]) || 0;
  const total = of(lbl.totalByMonth),
    corr = of(lbl.corrByMonth),
    nonFact = of(lbl.nonFactByMonth);
  return {
    corrective: correctiveHours(total, corr, nonFact, isEvol || shared),
    // Évolutive : non taggé + non_facturable ; sinon le tag non_facturable.
    nonBillable: isEvol ? Math.max(0, total - corr - of(lbl.evolByMonth)) : nonFact,
    evolutive: isEvol ? of(lbl.evolByMonth) : null,
    // Corrective partagée : le non taggé appartient à l'autre phase du même projet Jira.
    otherPhase: shared && !isEvol ? Math.max(0, total - corr - nonFact) : null,
  };
}

export function maintenanceQuotaUse(
  phase: Phase,
  lbl: MaintenanceLabelHours | null | undefined,
  shared: boolean,
  todayIso: DateIso,
) {
  const isEvol = phase.facturation === 'maintenance_evolutive';
  // Jira en erreur : pas de consommé (et non 0 h), recette du 06/10/2026.
  const ready = !!lbl && !!lbl.loaded && !lbl.loading && !lbl.error;
  const loading = !!lbl?.loading;
  const month = todayIso.slice(0, 7);
  const hours = ready ? monthHours(lbl!, month, isEvol, shared) : null;
  const year = currentContractYear(phase.dateDebut, phase.dateFin, todayIso);
  const corrUnit = phase.correctifUnite || 'heures_mois';
  const corrYearH = ready
    ? correctiveHours(lbl!.totalYearH, lbl!.corrYearH, lbl!.nonFactYearH, isEvol || shared)
    : null;
  const evolH = ready ? (lbl!.evolYearH ?? null) : null;
  return {
    month,
    corrective: quotaUse(hours?.corrective ?? null, phase.correctifQte, corrUnit, 1, loading),
    // Le report saisi à la reconduction s'ajoute aux quotas de l'année qui le reçoit.
    correctiveYear: yearQuotaUse(
      corrYearH,
      phase.correctifQte,
      corrUnit,
      year,
      todayIso,
      loading,
      year ? carriedInto(phase, year.from, 'correctif') : 0,
    ),
    nonBillableH: hours?.nonBillable ?? null,
    otherPhaseH: hours?.otherPhase ?? null,
    // Évolutif du mois face à 1/12 du quota annuel (arbitrage du 14/10/2026).
    evolutiveMonth: isEvol
      ? quotaUse(
          hours?.evolutive ?? null,
          phase.evolutifQte,
          phase.evolutifUnite || 'jours_an',
          1,
          loading,
        )
      : null,
    evolutive: isEvol
      ? yearQuotaUse(
          evolH,
          phase.evolutifQte,
          phase.evolutifUnite || 'jours_an',
          year,
          todayIso,
          loading,
          year ? carriedInto(phase, year.from, 'evolutif') : 0,
        )
      : null,
  };
}
